import {CoworkBarePanel} from '../../../components/admin/CoworkFormPanel';
import LumaDialog from '../../../components/admin/LumaDialog';
import { consultarDocumentoReserva } from '../../../services/facturacionService';
import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import ReactDOM from 'react-dom';
import { createPago, fetchCitasByClienteIda } from '../../../services/pagosService';
import { TipoPago, fetchTiposPagoPorPos } from '../../../services/tiposPagoService';
import { obtenerSesionActivaPorId, SesionActivaResponse } from '../../../services/pos/sesionPOSService';
import { fetchPuntoDeVenta } from '../../../services/pos/puntoDeVentaService';
import { fetchCitaById } from '../../../services/citasService';
import { Cita } from '../../../services/citasService';
import DocumentoCobroPreview, {FormatoDocumento} from '../../../components/posCita/DocumentoCobroPreview';
import '../pos/coworkingPOS.css';
import {
    Banknote, CreditCard, X, Smartphone, Wallet, Receipt, FileText,
    CheckCircle, AlertCircle, ExternalLink, Lock, SplitSquareHorizontal,
} from 'lucide-react';

// ─── Design tokens ────────────────────────────────────────────
const C = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryDark:  '#004d7a',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const defaultPalette = C;
const coworkingPalette = {...C, primary: 'var(--ad-primary, #0b513e)', primaryDark: 'var(--ad-primary-dark, #073f30)', secondary: 'var(--ad-primary, #0b513e)', secondaryDark: 'var(--ad-primary-dark, #073f30)', secondaryLight: 'var(--ad-secondary, #8bbda3)'};

const css = `

.pm-wrap * { font-family:'DM Sans',sans-serif !important; box-sizing:border-box; }
@keyframes pmFadeUp  { from{opacity:0;transform:translateY(16px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes pmSpin    { to{transform:rotate(360deg);} }
@keyframes pmPulse   { 0%,100%{opacity:1;}50%{opacity:.55;} }
@keyframes pmSlideIn { from{opacity:0;transform:translateX(-8px);}to{opacity:1;transform:translateX(0);} }
.pm-doc-options { display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-top:12px; }
.pm-doc-option { position:relative;cursor:pointer;min-width:0; }
.pm-doc-option input { position:absolute;opacity:0;width:1px;height:1px; }
.pm-doc-card { display:flex;align-items:flex-start;gap:12px;height:100%;padding:16px;border:1.5px solid var(--ad-line, #e2e8f0);border-radius:16px;background:var(--ad-surface, #fff);transition:background .2s,border-color .2s,box-shadow .2s,transform .2s; }
.pm-doc-icon { display:flex;align-items:center;justify-content:center;flex-shrink:0;width:40px;height:40px;border-radius:12px;background:var(--ad-line, #f1f5f9);color:var(--ad-muted, #64748b); }
.pm-doc-check { margin-left:auto;color:transparent;flex-shrink:0; }
.pm-doc-option input:checked + .pm-doc-card { border-color:var(--doc-color);background:var(--doc-bg);box-shadow:0 4px 14px var(--doc-shadow); }
.pm-doc-option input:checked + .pm-doc-card .pm-doc-icon { background:var(--doc-color);color:#fff; }
.pm-doc-option input:checked + .pm-doc-card .pm-doc-check { color:var(--doc-color); }
.pm-doc-option input:focus-visible + .pm-doc-card { outline:3px solid var(--doc-color);outline-offset:3px; }
.pm-doc-option input:not(:disabled) + .pm-doc-card:hover { transform:translateY(-2px);border-color:var(--doc-color); }
.pm-doc-option input:disabled + .pm-doc-card { opacity:.55;cursor:not-allowed; }
@media(max-width:520px){.pm-doc-options{grid-template-columns:1fr}.pm-doc-card{padding:12px}}
@media(prefers-reduced-motion:reduce){.pm-doc-card{transition:none}.pm-doc-option input:not(:disabled) + .pm-doc-card:hover{transform:none}}
.pm-modal { animation:pmFadeUp .32s cubic-bezier(.16,1,.3,1) both; }
.pm-spin   { animation:pmSpin .7s linear infinite; }
.pm-pulse  { animation:pmPulse 1.8s ease-in-out infinite; }
.pm-slide  { animation:pmSlideIn .25s ease both; }
.pm-input { transition:border-color .2s, box-shadow .2s; }
.pm-input:focus { outline:none; border-color:${C.secondary} !important; box-shadow:0 0 0 3px color-mix(in srgb, ${C.secondary} 9.41%, transparent) !important; }
.pm-input:read-only { background:var(--ad-soft, #f8fafc) !important; }
.pm-input:disabled { opacity:.55; cursor:not-allowed; }
.pm-scroll::-webkit-scrollbar{width:5px;}
.pm-scroll::-webkit-scrollbar-track{background:transparent;}
.pm-scroll::-webkit-scrollbar-thumb{background:color-mix(in srgb, ${C.secondary} 18.82%, transparent);border-radius:10px;}
.pm-scroll::-webkit-scrollbar-thumb:hover{background:color-mix(in srgb, ${C.secondary} 33.33%, transparent);}
.pm-tipo-btn { transition:all .22s cubic-bezier(.16,1,.3,1); cursor:pointer; }
.pm-tipo-btn:hover { transform:translateY(-2px); }
.pm-btn-ghost { transition:all .18s; }
.pm-btn-ghost:hover { background:var(--ad-line, #f1f5f9) !important; }
.pm-btn-primary { transition:all .22s cubic-bezier(.16,1,.3,1); }
.pm-btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 24px color-mix(in srgb, ${C.secondary} 20.78%, transparent) !important; }
.pm-btn-primary:disabled { opacity:.5; cursor:not-allowed; transform:none !important; box-shadow:none !important; }
`;

interface PagosModalProps {
    appearance?: 'default' | 'coworking';
    displayMode?: 'modal' | 'panel';
    reservation?: Cita;
    formato?: FormatoDocumento;
    onFormato?: (f:FormatoDocumento)=>void;
    isOpen:        boolean;
    onClose:       () => void;
    citaId:        number;
    montoCita:     number;
    montoPagado:   number;
    /**
     * Caja EXPLÍCITA desde la que se cobra. Es la autoridad del flujo: el
     * modal la revalida por id al abrir y de nuevo justo antes de cobrar, y
     * nunca resuelve "la sesión activa" del operador por su cuenta.
     */
    sesionPosId:   number;
    /**
     * Grant del operador identificado por PIN sobre ``sesionPosId``. El backend
     * lo exige para cobrar y atribuye el asiento de caja a ese operador; sin
     * grant el cobro se bloquea en la UI (nunca se envía para fallar con 403).
     */
    operadorToken: string | null;
    /**
     * Fuera del layout POS (sin barra con selector de operador) el padre
     * gestiona el grant: este callback abre su diálogo de PIN.
     */
    onIdentifyOperator?: () => void;
    onPagoSuccess: () => void;
}

const PagosModal: React.FC<PagosModalProps> = ({
    isOpen, onClose, citaId, montoCita, montoPagado, sesionPosId, operadorToken, onIdentifyOperator, onPagoSuccess, appearance = 'default', displayMode = 'modal', reservation, formato, onFormato,
}) => {
    const formId = useId();
    const C = appearance === 'coworking' ? coworkingPalette : defaultPalette;
    const themedCss = appearance === 'coworking' ? css.replace(/#(?:c1348b|8f1f64|61257b|004d7a|8b3fa8)/gi, 'var(--ad-primary, #0b513e)') : css;
    // ── Estado ────────────────────────────────────────────────
    const [tiposPago,            setTiposPago]            = useState<TipoPago[]>([]);
    const [tipoPagoId,           setTipoPagoId]           = useState<number | ''>('');
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState<TipoPago | null>(null);
    const [efectivoRecibido,     setEfectivoRecibido]     = useState<number>(0);
    const [cambio,               setCambio]               = useState<number>(0);
    const [isLoading,            setIsLoading]            = useState(false);
    // Generación del grant con la que se inició el cobro en vuelo (null = ninguno).
    // "Procesando" se DERIVA de que esa generación siga siendo la vigente: un
    // cambio de operador la deja atrás en el mismo render, sin efecto tardío.
    const [processingGrant,      setProcessingGrant]      = useState<number | null>(null);
    const [error,                setError]                = useState<string | null>(null);
    const [saldoPendiente,       setSaldoPendiente]       = useState(montoCita - montoPagado);
    const [ultimoCambio,         setUltimoCambio]         = useState<number>(0);
    const [sesionActiva,         setSesionActiva]         = useState<SesionActivaResponse | null>(null);
    const [verificandoSesion,    setVerificandoSesion]    = useState(false);
    const [showNoSessionPopup,   setShowNoSessionPopup]   = useState(false);
    const documentoEditado = useRef(false);
    const [documentoReserva, setDocumentoReserva] = useState<'factura'|'prefactura'>('factura');
    useEffect(() => { let active=true; if(isOpen){documentoEditado.current=false;setDocumentoReserva('factura');consultarDocumentoReserva(Number(citaId)).then(d=>{if(active&&!documentoEditado.current)setDocumentoReserva(d.tipo);}).catch(()=>{});} return()=>{active=false;}; }, [isOpen, citaId]);
    const [paymentSuccess,       setPaymentSuccess]       = useState(false);
    const [montoAPagar,          setMontoAPagar]          = useState<number>(0);
    const [citaBranchName,       setCitaBranchName]       = useState<string | null>(null);
    const [posBranchName,        setPosBranchName]        = useState<string | null>(null);
    const [errorSucursal,        setErrorSucursal]        = useState<string | null>(null);
    const [localFormato,setLocalFormato] = useState<FormatoDocumento>('rollo');

    const redondear = useCallback((valor: number) =>
        Math.round((valor + Number.EPSILON) * 100) / 100, []);

    // ── Identidad de la operación: cita + sesión POS + operador + generación ──
    // Toda continuación asíncrona (carga de sesión/tipos/sucursal, cobro y
    // callbacks) exige que el modal siga montado y abierto, que citaId y
    // sesionPosId sigan siendo los capturados al iniciar y que ninguna nueva
    // identidad/cierre haya incrementado la generación mientras tanto. El
    // cobro exige ADEMÁS que el grant del operador (operadorToken) siga
    // siendo exactamente el capturado al iniciar y que ningún cambio de
    // operador haya incrementado la generación del grant: nunca se cobra con
    // un token viejo aunque el operador vuelva a ser el mismo (A -> B -> A).
    const mountedRef = useRef(true);
    const generationRef = useRef(0);
    const grantGenerationRef = useRef(0);
    const identityRef = useRef({ isOpen, citaId, sesionPosId, operadorToken });
    identityRef.current = { isOpen, citaId, sesionPosId, operadorToken };
    // Cerrojo SÍNCRONO de envío: se adquiere antes del primer await y solo lo
    // libera la misma generación (identidad + grant) que lo tomó: una
    // respuesta tardía de una generación anterior jamás desbloquea la actual.
    // Un cambio de identidad/cierre/operador lo reinicia junto con la generación.
    const submittingRef = useRef(false);
    // Cambio de operador (grant) detectado EN ESTE RENDER, no en un efecto:
    // el padre entrega el token por estado (PIN resuelto fuera de un evento) y
    // React pinta el botón habilitado antes de correr los efectos pasivos de ese
    // commit. Si el operador pulsa en esa ventana, un efecto tardío incrementaría
    // la generación DESPUÉS de que el submit la capturó y abortaría un cobro con
    // el token vigente. Aquí la generación del grant ya está actualizada cuando
    // se pinta el botón, así que todo submit posterior la captura correcta. El
    // cambio invalida cualquier cobro en vuelo y libera el cerrojo para el nuevo
    // operador SIN recargar caja, tipos ni sucursal (la cita y la caja no
    // cambiaron). La respuesta tardía del grant anterior ya no es "actual": ni
    // cobra, ni notifica, ni libera.
    const lastGrantRef = useRef(operadorToken);
    if (lastGrantRef.current !== operadorToken) {
        lastGrantRef.current = operadorToken;
        grantGenerationRef.current += 1;
        submittingRef.current = false;
    }
    const isProcessingPayment = processingGrant !== null && processingGrant === grantGenerationRef.current;
    const beginOperation = useCallback((options?: { grantToken: string }) => {
        const generation = generationRef.current;
        const grantGeneration = grantGenerationRef.current;
        const cita = citaId;
        const pos = sesionPosId;
        const grant = options?.grantToken;
        return () => mountedRef.current
            && generation === generationRef.current
            && identityRef.current.isOpen
            && identityRef.current.citaId === cita
            && identityRef.current.sesionPosId === pos
            && (grant === undefined || (
                grantGeneration === grantGenerationRef.current
                && identityRef.current.operadorToken === grant
            ));
    }, [citaId, sesionPosId]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            generationRef.current += 1;
            grantGenerationRef.current += 1;
        };
    }, []);

    useEffect(() => {
        // Nueva identidad (cita/sesión) o cierre: invalida TODO lo anterior y
        // reinicia el estado. Nada de la identidad previa sobrevive.
        generationRef.current += 1;
        grantGenerationRef.current += 1;
        submittingRef.current = false;
        setProcessingGrant(null); setPaymentSuccess(false); setError(null); setErrorSucursal(null);
        setSesionActiva(null); setPosBranchName(null); setCitaBranchName(null); setShowNoSessionPopup(false);
        setTiposPago([]); setTipoPagoId(''); setTipoPagoSeleccionado(null); setUltimoCambio(0);
        if (!isOpen) { setVerificandoSesion(false); setIsLoading(false); return; }
        const isCurrent = beginOperation();

        const cargarSesionYTipos = async () => {
            setVerificandoSesion(true);
            let sesion: SesionActivaResponse | null = null;
            try {
                // Autoridad EXPLÍCITA: se revalida la sesión indicada por id.
                sesion = await obtenerSesionActivaPorId(sesionPosId);
                if (!isCurrent()) return;
                if (!sesion || sesion.id !== sesionPosId) {
                    setSesionActiva(null); setShowNoSessionPopup(true); return;
                }
                setSesionActiva(sesion);
            } catch {
                if (!isCurrent()) return;
                setSesionActiva(null); setShowNoSessionPopup(true); return;
            } finally {
                if (isCurrent()) setVerificandoSesion(false);
            }
            setIsLoading(true);
            try {
                const pos = await fetchPuntoDeVenta(sesion.punto_de_venta);
                if (!isCurrent()) return;
                setPosBranchName(pos.sucursal_info?.nombre || 'Desconocida');
                const tipos = await fetchTiposPagoPorPos(sesion.punto_de_venta);
                if (!isCurrent()) return;
                setTiposPago(tipos);
                if (tipos.length > 0) {
                    const primerTipo = tipos[0];
                    setTipoPagoId(primerTipo.id);
                    setTipoPagoSeleccionado(primerTipo);
                    const saldo = redondear(montoCita - montoPagado);
                    setMontoAPagar(saldo);
                    setEfectivoRecibido(saldo);
                }
            } catch (err) {
                if (!isCurrent()) return;
                setError('Error al cargar los tipos de pago');
                /* Browser diagnostics omitted. */
            } finally {
                if (isCurrent()) setIsLoading(false);
            }
        };

        cargarSesionYTipos();
        fetchCitaById(citaId)
            .then(cita => { if (isCurrent()) setCitaBranchName(cita.sucursal_info?.nombre || 'Desconocida'); })
            .catch(() => { if (isCurrent()) setErrorSucursal('No se pudo verificar la sucursal del cliente.'); });
        fetchCitasByClienteIda(citaId)
            .then(pagos => { if (isCurrent()) setUltimoCambio(pagos.length > 0 ? Number(pagos[0].cambio) || 0 : 0); })
            .catch(() => { if (isCurrent()) setUltimoCambio(0); });
        // montoCita/montoPagado solo inicializan importes: no reinician la carga.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, citaId, sesionPosId, beginOperation]);

    useEffect(() => {
        const saldo = redondear(montoCita - montoPagado);
        setSaldoPendiente(saldo);
        if (tipoPagoSeleccionado?.configuracion_detail) {
            const config = tipoPagoSeleccionado.configuracion_detail;
            if (documentoReserva === 'factura' || !config.permite_monto_personalizado) setMontoAPagar(saldo);
            if (config.requiere_cambio) {
                setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
            } else if (config.permite_monto_personalizado && !config.afecta_caja && efectivoRecibido > montoAPagar) {
                setCambio(redondear(efectivoRecibido - montoAPagar));
            } else {
                setCambio(0);
                if (!config.afecta_caja && !config.permite_monto_personalizado) setEfectivoRecibido(redondear(montoAPagar));
            }
        } else {
            setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
        }
    }, [efectivoRecibido, montoCita, montoPagado, montoAPagar, tipoPagoSeleccionado, redondear, documentoReserva]);

    const selectTipoPago = (id: number) => {
        setTipoPagoId(id);
        const tipo = tiposPago.find(t => t.id === id);
        setTipoPagoSeleccionado(tipo || null);
        const saldo = redondear(montoCita - montoPagado);
        setMontoAPagar(saldo);
        setEfectivoRecibido(saldo);
    };
    const handleTipoPagoChange = (e: React.ChangeEvent<HTMLSelectElement>) => selectTipoPago(Number(e.target.value));

    const getIconoTipoPago = (tipo: string) => {
        switch (tipo) {
            case 'efectivo': return <Banknote size={16}/>;
            case 'banco':    return <CreditCard size={16}/>;
            case 'otro':     return <Smartphone size={16}/>;
            default:         return <Wallet size={16}/>;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Doble envío (dos submits/clicks en el mismo turno, Enter + click…):
        // idempotente en la UI, una sola petición en vuelo por generación.
        if (submittingRef.current || isProcessingPayment) return;
        setError(null); setErrorSucursal(null); setPaymentSuccess(false);
        if (!sesionActiva || sesionActiva.id !== sesionPosId) { setError('No hay sesión POS activa'); return; }
        if (!operadorToken) { setError('Identifica al operador de caja con su PIN antes de cobrar.'); return; }
        const grantToken = operadorToken;
        if (citaBranchName && posBranchName && citaBranchName !== posBranchName) {
            setErrorSucursal(`No es posible registrar el pago porque la sesión POS activa pertenece a la sucursal "${posBranchName}", mientras que el cliente pertenece a la sucursal "${citaBranchName}".\n\nPor favor, cierre la sesión POS actual y abra una nueva en la sucursal "${citaBranchName}".`);
            return;
        }
        if (!tipoPagoId || !tipoPagoSeleccionado) { setError('Por favor seleccione un tipo de pago'); return; }
        const config = tipoPagoSeleccionado.configuracion_detail;
        const efectivoRedondeado = redondear(efectivoRecibido);
        const montoFinal = redondear(montoAPagar);
        if (config) {
            if (config.requiere_cambio) {
                if (efectivoRedondeado <= 0) { setError('El efectivo recibido debe ser mayor a 0'); return; }
                if (efectivoRedondeado < montoFinal) { setError('El efectivo recibido no puede ser menor al monto a pagar'); return; }
            }
            if (config.permite_monto_personalizado && montoFinal <= 0) { setError('El monto a pagar debe ser mayor a 0'); return; }
            if (config.permite_monto_personalizado && montoFinal > redondear(montoCita - montoPagado)) { setError(`El monto no puede superar el saldo pendiente de ${redondear(montoCita - montoPagado).toFixed(2)} Bs.`); return; }
        }
        if (documentoReserva === 'factura' && montoFinal !== redondear(montoCita - montoPagado)) {
            setError('Para factura debes cubrir el saldo completo de la reserva.'); return;
        }
        if (montoFinal <= 0) { setError('Debe ingresar un monto mayor a 0'); return; }
        // Identidad capturada al iniciar: cita + caja explícita + grant del
        // operador. Si cambia cualquiera (o el modal se cierra/desmonta)
        // durante una espera, la continuación se abandona: ni cobro ni
        // callbacks bajo la anterior, y nunca un createPago con token viejo.
        const targetCitaId = citaId;
        const targetPosId = sesionPosId;
        const isCurrent = beginOperation({ grantToken });
        // Cerrojo adquirido ANTES del primer await (síncrono en este turno).
        submittingRef.current = true;
        setProcessingGrant(grantGenerationRef.current);
        try {
            // Revalidar la MISMA caja por id justo antes de mutar (abortable).
            const vigente = await obtenerSesionActivaPorId(targetPosId);
            if (!isCurrent()) return;
            if (!vigente || vigente.id !== targetPosId) {
                setProcessingGrant(null); setSesionActiva(null); setShowNoSessionPopup(true); return;
            }
            const datosPago: any = { documento_reserva: documentoReserva, cita: targetCitaId, tipo_pago: tipoPagoId, monto: montoFinal, sesion_pos: targetPosId };
            if (config?.requiere_cambio) datosPago.efectivo_recibido = efectivoRedondeado;
            else if (config?.permite_monto_personalizado && !config?.afecta_caja && efectivoRedondeado > montoFinal) datosPago.efectivo_recibido = efectivoRedondeado;
            else if (config?.afecta_caja) datosPago.efectivo_recibido = montoFinal;
            // Justo antes de mutar: identidad y grant del operador vigentes
            // (el token enviado es EXACTAMENTE el que sigue identificado).
            if (!isCurrent()) return;
            await createPago(datosPago, grantToken);
            if (!isCurrent()) return;
            // Éxito SOLO tras la confirmación del servidor y solo para esta
            // identidad: se notifica de inmediato, sin espera cosmética.
            setPaymentSuccess(true); setProcessingGrant(null);
            onPagoSuccess(); onClose();
        } catch (err: any) {
            if (!isCurrent()) return;
            setProcessingGrant(null); setPaymentSuccess(false);
            if (err?.response?.status === 409 || err?.response?.status === 404) { setSesionActiva(null); setShowNoSessionPopup(true); return; }
            if (err.response?.data?.error) setError(err.response.data.error);
            else if (err.response?.data) setError(Object.values(err.response.data).flat().join(' '));
            else setError('Error al registrar el pago');
        } finally {
            // Solo la generación que lo adquirió libera el cerrojo.
            if (isCurrent()) submittingRef.current = false;
        }
    };

    const handleIrAPOS       = () => { onClose(); window.open('/pos-list', '_blank'); };
    const handleAbrirSesionPOS = () => { onClose(); window.open('/pos-list', '_blank'); };
    const handleCloseNoSessionPopup = () => { setShowNoSessionPopup(false); onClose(); };

    if (!isOpen) return null;

    // ─── Helpers de color por tipo de config ─────────────────
    const configColor = (afecta: boolean, requiere: boolean) => {
        if (!afecta)           return { bg: '#7c3aed', light: '#ede9fe', border: '#c4b5fd' };
        if (afecta && requiere) return { bg: C.secondary, light: `color-mix(in srgb, ${C.secondary} 7.06%, transparent)`, border: `color-mix(in srgb, ${C.secondary} 18.82%, transparent)` };
        return { bg: C.secondaryLight, light: `color-mix(in srgb, ${C.secondaryLight} 7.06%, transparent)`, border: `color-mix(in srgb, ${C.secondaryLight} 18.82%, transparent)` };
    };

    const config            = tipoPagoSeleccionado?.configuracion_detail;
    const permitePersonalizado = documentoReserva === 'prefactura' && (config?.permite_monto_personalizado ?? false);
    const esPagoDigitalConCambio = permitePersonalizado && !config?.afecta_caja && efectivoRecibido > montoAPagar;

    // ─── Shared input style ───────────────────────────────────
    const inputBase: React.CSSProperties = {
        width: '100%', padding: '10px 14px', borderRadius: '10px',
        border: '1.5px solid var(--ad-line, #e2e8f0)', fontSize: '.88rem', color: 'var(--ad-ink, #0f172a)',
        background: 'var(--ad-surface, #fff)', fontFamily: "'DM Sans',sans-serif", fontWeight: '500',
    };

    if (appearance === 'coworking') {
        const disabled = isLoading || verificandoSesion || !sesionActiva || !operadorToken || isProcessingPayment || !!errorSucursal || showNoSessionPopup;
        const body = <div className="cw-payment">
            <form onSubmit={handleSubmit} aria-label="Registrar cobro de reserva"><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                <h3>Registrar cobro</h3>
                {verificandoSesion&&<p role="status">Verificando la caja…</p>}
                {(error||errorSucursal)&&<p role="alert" className="cw-payment-error">{errorSucursal||error}</p>}
                {showNoSessionPopup&&<p role="alert" className="cw-payment-error">La sesión de caja ya no está disponible. Vuelve al punto de venta para continuar.</p>}
                {!operadorToken&&<p className="cw-payment-error">Identifica al operador con su PIN antes de cobrar.{onIdentifyOperator&&<button type="button" className="rw-button" onClick={onIdentifyOperator}>Identificar operador</button>}</p>}
                <fieldset disabled={disabled} className="cw-payment-document"><legend>Documento</legend>{(['prefactura','factura'] as const).map(type=><label key={type}><input type="radio" name="documento-reserva" aria-label={type==='factura'?'Factura':'Prefactura'} checked={documentoReserva===type} onChange={()=>{documentoEditado.current=true;setDocumentoReserva(type);}}/>{type==='factura'?'Factura':'Prefactura'}</label>)}</fieldset>
                <p className="cw-help">{documentoReserva==='factura'?'Requiere cubrir el saldo completo para emitir la factura.':'Permite un anticipo cuando el método de pago admite montos parciales.'}</p>
                <label className="cw-payment-field">Monto a cobrar (Bs)<input aria-label="Monto a pagar" type="number" min="0.01" step="0.01" max={redondear(montoCita-montoPagado)} value={montoAPagar} disabled={disabled} readOnly={!permitePersonalizado} onChange={e=>{if(permitePersonalizado){const value=redondear(Math.min(Math.max(0,Number(e.target.value)||0),montoCita-montoPagado));setMontoAPagar(value);if(!config?.requiere_cambio)setEfectivoRecibido(value);}}}/></label>
                <fieldset disabled={disabled} className="cw-payment-methods"><legend>Método de pago</legend>{tiposPago.map(type=><button key={type.id} type="button" aria-pressed={tipoPagoId===type.id} onClick={()=>selectTipoPago(type.id)}>{getIconoTipoPago(type.configuracion_detail?.tipo||'otro')}<span>{type.nombre}</span></button>)}</fieldset>
                {!isLoading&&!verificandoSesion&&!tiposPago.length&&<p role="alert">No hay métodos de pago disponibles para esta caja.</p>}
                {(config?.requiere_cambio||(config?.permite_monto_personalizado&&!config?.afecta_caja))&&<label className="cw-payment-field">{config?.requiere_cambio?'Efectivo recibido':'Monto recibido'} (Bs)<input aria-label="Monto recibido" type="number" min="0" step="0.01" value={efectivoRecibido} disabled={disabled} onChange={e=>setEfectivoRecibido(Math.max(0,Number(e.target.value)||0))}/></label>}
                {(config?.requiere_cambio||esPagoDigitalConCambio)&&<div className="cw-payment-change"><span>Cambio a entregar</span><strong>Bs {cambio.toFixed(2)}</strong></div>}
                <div className="cw-actions"><button className="rw-button rw-primary btn btn-primary" type="submit" disabled={disabled||saldoPendiente<=0||!tipoPagoId}>{isProcessingPayment?'Registrando…':'Registrar pago'}</button>{displayMode==='modal'&&<button className="rw-button" type="button" disabled={isProcessingPayment} onClick={onClose}>Cancelar</button>}</div>
            </form>
            <DocumentoCobroPreview cita={reservation||{id:citaId} as Cita} total={montoCita} pagado={montoPagado} monto={montoAPagar} tipo={documentoReserva} formato={formato||localFormato} onFormato={onFormato||setLocalFormato}/>
        </div>;
        if (displayMode === 'panel') return body;
        return ReactDOM.createPortal(<div className="cw-payment-overlay"><CoworkBarePanel className="cw-payment-dialog" role="dialog" aria-modal="true" aria-label="Cobrar reserva"><button className="rw-button" aria-label="Cerrar cobro" disabled={isProcessingPayment} onClick={onClose}><X size={18}/></button>{body}</CoworkBarePanel></div>, document.body);
    }

    const disabled = isLoading || verificandoSesion || !sesionActiva || !operadorToken || isProcessingPayment || !!errorSucursal || showNoSessionPopup;
    const close = () => { if (!isProcessingPayment) onClose(); };
    return <LumaDialog open={isOpen} title={showNoSessionPopup?'Sesión POS Requerida':'Registrar pago'} onClose={close} footer={<>
        <button type="button" className="vx-button vx-button-secondary" disabled={isProcessingPayment} onClick={close}>Cancelar</button>
        {!showNoSessionPopup&&<button form={formId} type="submit" className="vx-button vx-button-primary" disabled={disabled||saldoPendiente<=0||!tipoPagoId}>{isProcessingPayment?'Registrando…':'Registrar pago'}</button>}
    </>}>
        {verificandoSesion?<p role="status">Verificando sesión POS activa…</p>:showNoSessionPopup?<div className="luma-payment-empty"><Wallet size={30}/><strong>Necesitas una caja abierta para cobrar</strong><p>Abre o reanuda una sesión del punto de venta y vuelve a registrar el pago.</p><button className="vx-button vx-button-primary" onClick={handleAbrirSesionPOS}>Abrir Sesión POS</button><button className="vx-button vx-button-secondary" onClick={handleIrAPOS}>Ver estado actual del POS</button></div>:<form id={formId} onSubmit={handleSubmit} className="luma-payment-form" aria-label="Registrar pago de reserva">
            <div className="luma-payment-session"><div><strong>{sesionActiva?.punto_de_venta_nombre||'Caja de la reserva'}</strong><p>Sesión POS activa · {sesionActiva?.codigo}</p></div>{!operadorToken&&onIdentifyOperator&&<button type="button" className="vx-button vx-button-secondary" onClick={onIdentifyOperator}>Identificar operador</button>}{operadorToken&&<span className="luma-detail-note">Operador identificado</span>}</div>
            {!operadorToken&&<p role="note" className="luma-feedback">Identifica al operador de caja con su PIN para cobrar.</p>}
            {(error||errorSucursal)&&<p role="alert" className="luma-feedback">{errorSucursal||error}</p>}
            {paymentSuccess&&<p role="status" className="luma-feedback">Pago registrado exitosamente.</p>}
            <div className="luma-payment-summary">{[['Total de la reserva',montoCita],['Pagado',montoPagado],['Saldo pendiente',Math.max(0,saldoPendiente)]].map(([label,value])=><div key={String(label)}><span>{label}</span><strong>Bs {Number(value).toFixed(2)}</strong></div>)}</div>
            <fieldset className="luma-payment-documents" disabled={disabled}><legend>¿Qué documento necesita el cliente?</legend><div>{(['factura','prefactura']as const).map(type=><label key={type}><input type="radio" name="documento-reserva" aria-label={type==='factura'?'Factura':'Prefactura'} checked={documentoReserva===type} onChange={()=>{documentoEditado.current=true;setDocumentoReserva(type);}}/><span><strong>{type==='factura'?'Factura':'Prefactura'}</strong><small>{type==='factura'?'Cobrar el saldo completo y emitir factura.':'Registrar un anticipo o cobro con opción de facturar después.'}</small></span></label>)}</div></fieldset>
            <label className="luma-payment-field">Método de pago<select aria-label="Tipo de pago" value={tipoPagoId} disabled={disabled} onChange={e=>selectTipoPago(Number(e.target.value))}><option value="" disabled>Selecciona un método</option>{tiposPago.map(type=><option key={type.id} value={type.id}>{type.nombre}</option>)}</select></label>
            {!isLoading&&!tiposPago.length&&<p role="alert">No hay métodos de pago disponibles para esta caja.</p>}
            <div className="luma-payment-fields"><label className="luma-payment-field">Monto a cobrar (Bs)<input aria-label="Monto a pagar" type="number" min="0.01" step="0.01" max={Math.max(0,redondear(montoCita-montoPagado))} value={montoAPagar} disabled={disabled} readOnly={!permitePersonalizado} onChange={e=>{if(permitePersonalizado){const value=redondear(Math.min(Math.max(0,Number(e.target.value)||0),montoCita-montoPagado));setMontoAPagar(value);if(!config?.requiere_cambio)setEfectivoRecibido(value);}}}/><small className="luma-detail-note">{permitePersonalizado?'Puedes registrar un pago parcial.':'Se cobrará el saldo completo.'}</small></label>
            {(config?.requiere_cambio||(config?.permite_monto_personalizado&&!config?.afecta_caja))&&<label className="luma-payment-field">{config?.requiere_cambio?'Efectivo recibido':'Monto recibido'} (Bs)<input aria-label="Monto recibido" type="number" min="0" step="0.01" value={efectivoRecibido} disabled={disabled} onChange={e=>setEfectivoRecibido(Math.max(0,Number(e.target.value)||0))}/></label>}</div>
            {(config?.requiere_cambio||esPagoDigitalConCambio)&&<div className="luma-payment-change"><span>Cambio a entregar</span><strong>Bs {cambio.toFixed(2)}</strong></div>}
        </form>}
    </LumaDialog>;

};

export default PagosModal;




